import { Combobox, ComboboxRootProps } from "@base-ui/react";
import { Chip, tokens } from "@blurb/codex-react";
import { createStyles } from "@mantine/core";
import { IconV3 } from "@rpi/openapi-core";
import React from "react";

const useMultiselectStyles = createStyles({
  container: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacing[2],
  },
  positioner: {
    zIndex: 500,
  },
  popup: {
    backgroundColor: tokens.color.semantic.bg.surfaceRaised,
    padding: tokens.spacing[2],
    borderRadius: tokens.radius.sm,
    minWidth: 250,
    boxShadow: tokens.shadow.raised,
  },
  chips: {
    display: "flex",
    flexWrap: "wrap",
    columnGap: tokens.spacing[1],
    rowGap: tokens.spacing[2],
  },
  item: {
    display: "flex",
    justifyContent: "space-between",
    color: tokens.color.semantic.text.bold,
    padding: tokens.spacing[2],
    cursor: "pointer",
    gap: tokens.spacing[2],

    "&:hover, &[data-highlighted]": {
      backgroundColor: tokens.color.semantic.bg.inputHover,
      borderRadius: tokens.radius.md,
    },
  },
  selectedIcon: {
    width: tokens.spacing[5],
    height: tokens.spacing[5],
    color: tokens.color.semantic.icon.bold,
  },
  label: {
    color: tokens.color.semantic.text.bold,
    fontFamily: tokens.font.family.sans,
    fontSize: tokens.font.size.base,
    "font-weight": tokens.font.weight.semibold,
    lineHeight: tokens.font.lineHeight.normal,
  },
  inputWrapper: {
    position: "relative" as const,
    display: "flex",
    alignItems: "center",
  },
  input: {
    color: tokens.color.semantic.text.bold,
    fontFamily: tokens.font.family.sans,
    fontSize: tokens.font.size.base,
    lineHeight: tokens.font.lineHeight.normal,
    borderColor: tokens.color.semantic.border.subtle,
    borderStyle: "solid",
    borderWidth: 1,
    borderRadius: tokens.radius.sm,
    padding: tokens.spacing[2],
    paddingRight: tokens.spacing[8],
    width: "100%",

    "&:disabled": {
      backgroundColor: tokens.color.semantic.bg.neutralDisabled,
    },

    "&:hover": {
      backgroundColor: tokens.color.semantic.bg.inputHover,
    },

    "&::placeholder": {
      color: tokens.color.semantic.text.subtle,
    },
  },
  chevron: {
    position: "absolute" as const,
    right: tokens.spacing[2],
    pointerEvents: "none" as const,
    color: tokens.color.semantic.icon.subtle,
  },
  noResults: {
    padding: tokens.spacing[2],
    color: tokens.color.semantic.text.bold,
    fontFamily: tokens.font.family.sans,
    fontSize: tokens.font.size.base,
    lineHeight: tokens.font.lineHeight.snug,
    display: "flex",
    justifyContent: "center",
  },
});

export type MultiselectValue<TKey extends string> = {
  label?: string;
  id: TKey;
};

interface MultiselectInputProps<TKey extends string> extends ComboboxRootProps<MultiselectValue<TKey>, true> {
  label?: string;
  onDelete: (value: MultiselectValue<TKey>) => void;
}

function defaultValueEquality<TKey extends string>(itemValue: MultiselectValue<TKey>, value: MultiselectValue<TKey>) {
  return itemValue.id === value.id;
}

export function MultiselectInput<TKey extends string>({ isItemEqualToValue, ...props }: MultiselectInputProps<TKey>) {
  const { classes } = useMultiselectStyles();
  const containerRef = React.useRef<HTMLInputElement | null>(null);
  const id = React.useId();

  return (
    <Combobox.Root multiple isItemEqualToValue={isItemEqualToValue ?? defaultValueEquality} {...props}>
      <div className={classes.container}>
        {props.label && (
          <label className={classes.label} htmlFor={id}>
            {props.label}
          </label>
        )}
        <div className={classes.inputWrapper}>
          <Combobox.Input id={id} placeholder="Select..." className={classes.input} ref={containerRef} />
          <IconV3 icon="keyboard_arrow_down" className={classes.chevron} />
        </div>
        <Combobox.Chips className={classes.chips}>
          <Combobox.Value>
            {(values: MultiselectValue<TKey>[]) =>
              values.map((value) => (
                <Combobox.Chip
                  key={value.id}
                  render={() => (
                    <Chip
                      aria-label={value.label}
                      label={value.label ?? value.id}
                      onDelete={() => props.onDelete(value)}
                    />
                  )}
                />
              ))
            }
          </Combobox.Value>
        </Combobox.Chips>

        <Combobox.Portal>
          <Combobox.Positioner className={classes.positioner} anchor={containerRef}>
            <Combobox.Popup className={classes.popup}>
              <Combobox.Empty className={classes.noResults}>No results found.</Combobox.Empty>
              <Combobox.List>
                {(value: MultiselectValue<TKey>) => (
                  <Combobox.Item key={value.id} value={value} className={classes.item}>
                    <span>{value.label}</span>
                    <Combobox.ItemIndicator className={classes.selectedIcon}>
                      <IconV3 icon="check" />
                    </Combobox.ItemIndicator>
                  </Combobox.Item>
                )}
              </Combobox.List>
            </Combobox.Popup>
          </Combobox.Positioner>
        </Combobox.Portal>
      </div>
    </Combobox.Root>
  );
}
